import React, { useState, useEffect, useRef } from 'react';
import { array, bool, string, func, number, shape, arrayOf } from 'prop-types';
import classnames from 'classnames';
import sortBy from 'lodash/sortBy';
import { MultiSelect, Select } from '../../../../ps-aspect-design';
import { Toggle, Tooltip } from 'ps-design';
import './ReportFilters.scss';

const FORMAT_OPTIONS = [
  { key: 'All', text: 'All' },
  { key: 'Spreadsheet', text: 'Spreadsheet' },
  { key: 'Tableau', text: 'Tableau' }
];

const COLLECTION_TYPE_OPTIONS = [
  { key: 'All', text: 'All' },
  { key: 'Standard', text: 'Standard' },
  { key: 'Site', text: 'Site' },
  { key: 'Group', text: 'Group' }
];

const SORT_OPTIONS = [
  { key: 'Date: newest first', text: 'Date: newest first' },
  { key: 'Date: oldest first', text: 'Date: oldest first' },
  { key: 'Name: A to Z', text: 'Name: A to Z' },
  { key: 'Name: Z to A', text: 'Name: Z to A' }
];

ReportFilters.propTypes = {
  createdByFilter: number,
  defaultGroupId: string,
  favoriteFilter: bool,
  groupFunctionality: bool,
  groupId: string,
  includeFilter: string,
  initialReportsLoaded: bool,
  reports: shape({
    edges: array,
    totalCount: number
  }),
  reportTypes: arrayOf(
    shape({
      code: string,
      name: string
    })
  ),
  reportViewerRights: shape({
    allowViewGroup: bool,
    allowViewSite: bool,
    allowViewStandard: bool
  }),
  scopeFilter: string,
  setCreatedByFilter: func,
  setGroupIdFilter: func,
  setIncludeFilter: func,
  setScopeFilter: func,
  setSortField: func,
  setTypeFilter: func,
  setViewPreference: func,
  sortDirection: string,
  sortField: string,
  textFilter: string,
  userGroupAdmin: bool,
  userId: string,
  userReportViewer: bool,
  userSiteAdmin: bool,
  userSuperAdmin: bool,
  viewType: string,
  visualizationFunctionality: bool
};

function ReportFilters(props) {
  const {
    collections,
    collectionsFilter,
    collectionTypeFilter,
    createdByFilter,
    defaultGroupId,
    favoriteFilter,
    groupFunctionality,
    groupId,
    includeFilter,
    initialReportsLoaded,
    popularFilter,
    reports,
    reportTypes,
    reportViewerRights,
    scopeFilter,
    setCollectionsFilter,
    setCreatedByFilter,
    setGroupIdFilter,
    setIncludeFilter,
    setScopeFilter,
    setSortField,
    setTypeFilter,
    setViewPreference,
    sortDirection,
    sortField,
    textFilter,
    typeFilter,
    userGroupAdmin,
    userId,
    userReportViewer,
    userSiteAdmin,
    userSuperAdmin,
    viewType,
    visualizationFunctionality
  } = props;
  const [filteredReportTypes, setFilteredReportTypes] = useState([]);
  const [selectedReportTypes, setSelectedReportTypes] = useState([]);
  const [selectedReportCollections, setSelectedReportCollections] = useState([]);
  const [reportTypeOptions, setReportTypeOptions] = useState([]);
  const [reportCollectionOptions, setReportCollectionOptions] = useState([]);
  const [searchText, setSearchText] = useState('');
  const formatFilterRef = useRef();
  const sortFilterRef = useRef();
  const scopeFilterRef = useRef();
  const collectionTypeFilterRef = useRef();

  useEffect(() => {
    if (typeFilter) {
      const selectedTypes = reportTypes.filter(type => !typeFilter.includes(type.code)).map(type => type.name);
      setSelectedReportTypes(selectedTypes);
    }
  }, []);

  useEffect(() => {
    if (reports && reportTypes) {
      getFormattedReportTypes();
    }

    if (collections) {
      getReportCollectionsOptions();
    }
  }, [searchText, reports, collections, reportTypes]);

  function addReportTypeFilter(report, checked) {
    const selectedReport = reportTypes.find(type => type.name === report);
    let updatedSelections = selectedReportTypes;

    if (checked) {
      updatedSelections = [...updatedSelections, report];
    } else {
      updatedSelections = updatedSelections.filter(selectedReport => selectedReport !== report);
    }
    const selectedTypeCodes = reportTypes.filter(type => !updatedSelections.includes(type.name)).map(type => type.code);

    setSelectedReportTypes(updatedSelections);

    if (updatedSelections.length > 0) setTypeFilter(selectedTypeCodes);
    else setTypeFilter();
  }

  function updateCollectionReportFilter(collection, checked) {
    let updatedSelections = selectedReportCollections;

    if (checked) {
      updatedSelections = [...updatedSelections, collection];
    } else {
      updatedSelections = updatedSelections.filter(selectedReport => selectedReport !== collection);
    }

    setSelectedReportCollections(updatedSelections);

    if (updatedSelections.length > 0) setCollectionsFilter(updatedSelections);
    else setCollectionsFilter();
  }

  function clearReportTypeFilters() {
    setSelectedReportTypes([]);
    setTypeFilter();
  }

  function clearReportCollectionsFilters() {
    setSelectedReportCollections([]);
    setTypeFilter();
  }

  function clearReportCollectionFilters() {
    setSelectedReportCollections([]);
    setCollectionsFilter();
  }

  function clearSearch() {
    setSearchText('');
  }

  function getFormatFilterValue() {
    switch (includeFilter) {
      case 'TABULAR':
        return 'Spreadsheet';
        break;
      case 'VISUALIZATION':
        return 'Tableau';
        break;
      default:
        return 'All';
    }
  }

  function getFormattedReportTypes() {
    const formattedReportTypes = reportTypes.reduce((reports, type) => {
      if (type.count !== 0 && type.name.toLowerCase().includes(searchText.toLowerCase())) {
        reports.push({ text: type.name, value: type.code, count: type.count });
      }
      return reports;
    }, []);

    setReportTypeOptions(sortBy(formattedReportTypes));
  }

  function getReportCollectionsOptions() {
    let formattedCollectionOptions = collections;

    if (searchText) {
      formattedCollectionOptions = formattedCollectionOptions.filter(collection =>
        collection.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    formattedCollectionOptions = formattedCollectionOptions
      .map(collection => {
        return { text: collection.name, value: collection.id, count: collection.reports.length };
      })
      .filter(collection => collection.count !== 0);

    setReportCollectionOptions(formattedCollectionOptions);

    if (collectionsFilter) {
      const selectedCollections = formattedCollectionOptions
        .filter(collection => collectionsFilter.includes(collection.text))
        .map(type => type.text);
      setSelectedReportCollections(selectedCollections);
    }
  }

  function getScopeFilterValue() {
    let scopeFilterValue = scopeFilter;
    if (scopeFilterValue === 'personal' && createdByFilter) {
      scopeFilterValue = 'user';
    }

    switch (scopeFilterValue) {
      case 'personal':
        return 'Show All Personal';
        break;
      case 'site':
        return 'Site';
        break;
      case 'group':
        return 'Group';
        break;
      case 'user':
        return 'Personal';
        break;
      case 'internal':
        return 'Internal';
        break;
      default:
        return 'Standard';
    }
  }

  function getSortFilterValue() {
    if (sortField?.includes('lastModified')) {
      if (sortDirection?.includes('DESC')) {
        return 'Date: newest first';
      } else {
        return 'Date: oldest first';
      }
    } else if (sortField?.includes('name')) {
      if (sortDirection?.includes('DESC')) {
        return 'Name: Z to A';
      } else {
        return 'Name: A to Z';
      }
    } else {
      return 'Relevance';
    }
  }

  function handleSetCreatedByFilter(userId) {
    if (typeof userId === 'string') {
      userId = parseInt(userId);
    }
    setCreatedByFilter(userId);
  }

  function handleSetGroupIdFilter(groupId) {
    // Display all group reports when group is the default group
    if (groupId === defaultGroupId) {
      setGroupIdFilter(null);
    } else {
      setGroupIdFilter(groupId);
    }
  }

  function handleSetIncludeFilter(value) {
    setIncludeFilter(value);
  }

  function handleSetScopeFilter(value) {
    let transformedValue =
      value === 'Personal'
        ? 'user'
        : value === 'Group'
        ? 'group'
        : value === 'Standard'
        ? 'global'
        : value === 'Site'
        ? 'site'
        : value === 'Internal'
        ? 'internal'
        : 'personal';
    // Filter on both current user
    // and scope for personal reports
    if (transformedValue === 'user') {
      transformedValue = 'personal';
      handleSetCreatedByFilter(userId);
    } else {
      handleSetCreatedByFilter(null);
    }
    if (transformedValue === 'group' && groupId) {
      handleSetGroupIdFilter(groupId);
    } else {
      handleSetGroupIdFilter(null);
    }
    setScopeFilter(transformedValue);
  }

  function handleSetSortField(value) {
    const field = value?.includes('Date') ? 'lastModified' : value?.includes('Name') ? 'name' : 'score';
    const direction = value?.includes('newest') || value?.includes('Z to A') ? 'DESC' : 'ASC';

    setSortField(field, direction);
  }

  function isScopeFilterDisabled() {
    return !!favoriteFilter || !!textFilter;
  }

  function onSearchChange(event) {
    const value = event.target.value;
    setSearchText(value);
  }

  function renderScopeFilter(disableScopeFilter) {
    //  MPE-3043 Handle Report Viewer User Scope Options
    let scopeOptions = [];
    if (userReportViewer) {
      if (reportViewerRights.allowViewStandard) {
        scopeOptions.push({ text: 'Standard', key: 'Standard' });
      }
      if (reportViewerRights.allowViewSite) {
        scopeOptions.push({ text: 'Site', key: 'Site' });
      }
      if (reportViewerRights.allowViewGroup && groupFunctionality) {
        scopeOptions.push({ text: 'Group', key: 'Group' });
      }
    } else {
      scopeOptions = [
        { text: 'Standard', key: 'Standard' },
        { text: 'Personal', key: 'Personal' },
        { text: 'Site', key: 'Site' }
      ];

      if (groupFunctionality) {
        scopeOptions.push({ text: 'Group', key: 'Group' });
      }
      if (userSuperAdmin || userSiteAdmin) {
        scopeOptions.push({ text: 'Show All Personal', key: 'Show All Personal' });
      }
      if (userSuperAdmin) {
        scopeOptions.push({ text: 'Internal', key: 'Internal' });
      }
    }

    const scopeFilterValue = getScopeFilterValue();

    if (scopeOptions.length > 1) {
      return (
        <div className="reports__filter">
          <Select
            className="report-scope__filter"
            componentRef={scopeFilterRef}
            disabled={isScopeFilterDisabled()}
            handleChange={handleSetScopeFilter}
            id="report-scope"
            options={scopeOptions}
            value={scopeFilterValue}
          />
        </div>
      );
    } else {
      const match = scopeOptions.filter(x => x.value === scopeFilterValue);
      const text = match.length > 0 ? match[0].text : 'None Available';
      return <div className="reports__filter-scope--single">{text}</div>;
    }
  }

  function toggleView(viewType) {
    setViewPreference(viewType);
  }

  function renderReportFilters() {
    return (
      <div className="reports__filters">
        {!isScopeFilterDisabled() && (
          <React.Fragment>
            <label className="reports__filter-label" htmlFor="report-scope">
              Scope
            </label>
            {renderScopeFilter()}
          </React.Fragment>
        )}
        <label className="reports__filter-label" htmlFor="report-type">
          Report Types
        </label>
        <MultiSelect
          className="report-type__filter"
          label="Select one or more types"
          isSearchable={true}
          onCancel={() => {}}
          onClear={clearReportTypeFilters}
          options={reportTypeOptions}
          onSelectionChange={addReportTypeFilter}
          selectedOptions={selectedReportTypes}
          onSearchChange={onSearchChange}
          onClearSearch={clearSearch}
          searchValue={searchText}
          placeholder="Search Report Types"
        />
        {visualizationFunctionality && (
          <React.Fragment>
            <label className="reports__filter-label" htmlFor="report-visualizations">
              Format
            </label>
            <div className="reports__filter">
              <Select
                className="report-format__filter"
                componentRef={formatFilterRef}
                handleChange={handleSetIncludeFilter}
                id="report-visualizations"
                options={FORMAT_OPTIONS}
                value={getFormatFilterValue()}
              />
            </div>
          </React.Fragment>
        )}
        <div className="report-count">{`Viewing ${reports?.edges.length} Reports`}</div>
      </div>
    );
  }

  function renderPopularReportFilters() {
    return (
      <div className="reports__filters popular-reports__filters">
        <label className="reports__filter-label" htmlFor="report-collection">
          Collections
        </label>
        <MultiSelect
          className="report-collection__filter"
          label="Select one or more collections"
          id="collection-title__filter"
          isSearchable={true}
          onCancel={() => {}}
          onClear={clearReportCollectionFilters}
          options={reportCollectionOptions}
          onSelectionChange={updateCollectionReportFilter}
          selectedOptions={selectedReportCollections}
          onSearchChange={onSearchChange}
          onClearSearch={clearSearch}
          searchValue={searchText}
          placeholder="Search Report Collections"
        />
        <label className="reports__filter-label" htmlFor="report-collection__type">
          Collection Scope
        </label>
        <div className="reports__filter">
          <Select
            className="collection-type__filter"
            componentRef={collectionTypeFilterRef}
            handleChange={props.setCollectionTypeFilter}
            id="collection-type__filter"
            options={COLLECTION_TYPE_OPTIONS}
            value={collectionTypeFilter || 'All'}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classnames('reports__toolbar', { popular: popularFilter })}>
      {initialReportsLoaded && (
        <React.Fragment>
          {!popularFilter ? renderReportFilters() : renderPopularReportFilters()}
          {!popularFilter ? (
            <div className="reports__actions">
              {viewType !== 'list' && (
                <React.Fragment>
                  <label className="reports__filter-label" htmlFor="report-sort">
                    Sort
                  </label>
                  <div className="reports__filter">
                    <Select
                      className="report-sort__filter"
                      componentRef={sortFilterRef}
                      handleChange={handleSetSortField}
                      id="report-sort"
                      options={[
                        ...SORT_OPTIONS,
                        {
                          disabled: Boolean(!textFilter),
                          key: 'Relevance',
                          text: 'Relevance'
                        }
                      ]}
                      value={getSortFilterValue()}
                    />
                  </div>
                </React.Fragment>
              )}
              <button
                aria-label="View card layout for reports"
                className={classnames('reports__view-cards', { active: viewType === 'card' })}
                data-test-id="report-library-card-view-toggle"
                onClick={() => toggleView('card')}
              >
                <span className="icon-layout" />
              </button>
              <button
                aria-label="View table layout for reports"
                className={classnames('reports__view-table', { active: viewType === 'list' })}
                data-test-id="report-library-list-view-toggle"
                onClick={() => toggleView('list')}
              >
                <span className="icon-menu" />
              </button>
            </div>
          ) : (
            <div className="popular-reports__tooltips">
              <div className="collections-toggle__container">
                <Toggle
                  className="collections-toggle"
                  id="collections-toggle__details"
                  onChange={(id, checked) => props.toggleCollections(null, checked)}
                  value={props.hiddenCollections.length === 0}
                />
                <span className="collections-toggle__text">Expand All Collections</span>
              </div>
              <Tooltip
                display="underline"
                tooltipText="Collection Access Permission"
                hoverText="Access to each report in a collection is defined by the “Publish to” field on the Parameters tab within the report."
                id="collections-permissions-tooltip"
                position={userGroupAdmin || userSiteAdmin ? 'top' : 'left'}
              />

              {(userGroupAdmin || userSiteAdmin) && (
                <Tooltip
                  display="underline"
                  tooltipText="Create/Edit Collections"
                  hoverText="Go to the “All Reports” tab to create new custom collections or add new reports to existing custom collections. Hover over reports to select and use the “Add selected reports to” menu. You can change “Scope” and other filters to add more reports. Standard collections are not editable."
                  id="modify-collections-tooltip"
                  position="left"
                />
              )}
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
}

export default ReportFilters;
